import { GlobalOutlined, PlusCircleOutlined, SearchOutlined } from "@ant-design/icons";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { IconButton, Box } from "@mui/material";
import { Popconfirm, Space, message } from "antd";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../../../axios/axios-instance";
import ActionButton from "../../../components/common/buttons/ActionButton";
import SearchInput from "../../../components/common/fields/SearchInput";
import EndpointTranslationModal from "../../../components/common/EndpointTranslationModal";
import Card from "../../../components/common/Card";
import MuiTable from "../../../components/common/MuiTable";
import { Column } from "../../../utils/constants";
import { pathAPI } from "../../../utils/pathApi";
import { CreateSystemConfigModal, EditSystemConfigModal } from "../../../components/AddSystemConfig";
import { Switch } from "antd";

interface SystemConfig {
  id: number;
  config_key: string;
  config_value: string;
  description: string;
  status: number;
  created_date: string;
  last_modified_date: string;
}

interface SystemConfigListResponse {
  data: SystemConfig[];
  current_page: number;
  per_page: number;
  total: number;
}

const SystemConfig = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [openCreateModal, setOpenCreateModal] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [loadingDelete, setLoadingDelete] = useState(false);
  const [loadingTable, setLoadingTable] = useState(false);
  const [data, setData] = useState<SystemConfig[]>([]);
  const [editingConfig, setEditingConfig] = useState<SystemConfig | null>(null);
  const [loadingStatus, setLoadingStatus] = useState<number | null>(null);
  const [loadingConfig, setLoadingConfig] = useState(false);
  const [loadingEditIds, setLoadingEditIds] = useState<number[]>([]);
  const [selectedConfig, setSelectedConfig] = useState<SystemConfig | undefined>(undefined);
  const [translationModalVisible, setTranslationModalVisible] = useState(false);
  const [searchKeyword, setSearchKeyword] = useState("");
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(20);
  const [total, setTotal] = useState(0);

  const [messageApi, contextHolder] = message.useMessage();

  const handleClickEdit = async (id: number) => {
    try {
      setLoadingEditIds(prev => [...prev, id]);
      setLoadingConfig(true);
      const response = await axiosInstance.get(`${pathAPI.systemConfig}/${id}`);
      if (response.data?.data) {
        setEditingConfig(response.data.data);
        setOpenEditModal(true);
      }
    } catch (error) {
      message.error("Failed to load configuration details");
    } finally {
      setLoadingEditIds(prev => prev.filter(itemId => itemId !== id));
      setLoadingConfig(false);
    }
  };

  const handleManageTranslations = (config: SystemConfig) => {
    setSelectedConfig(config);
    setTranslationModalVisible(true);
  };

  const deleteSuccess = () => {
    messageApi.open({
      type: "success",
      content: "Delete item successfully!",
    });
  };

  const deleteError = () => {
    messageApi.open({
      type: "error",
      content: "Something went wrong!",
    });
  };

  const handleDelete = async (id: number) => {
    try {
      setLoadingDelete(true);
      await axiosInstance.delete(`${pathAPI.systemConfig}/${id}`);
      deleteSuccess();
      getDataTable(searchKeyword);
    } catch (error) {
      deleteError();
    } finally {
      setLoadingDelete(false);
    }
  };

  const handleStatusChange = async (id: number, newStatus: number) => {
    try {
      setLoadingStatus(id);
      await axiosInstance.put(`${pathAPI.systemConfig}/${id}/status?status=${newStatus}`);
      message.success('Status updated successfully!');
      getDataTable(searchKeyword);
    } catch (error) {
      console.error('Error updating status:', error);
      message.error('Failed to update status');
    } finally {
      setLoadingStatus(null);
    }
  };

  const header: Column[] = [
    {
      id: "id",
      label: "Id",
      minWidth: 10,
      align: "center",
    },
    {
      id: "config_key",
      label: "Key",
      minWidth: 150,
      align: "center",
      format: (value) => value,
    },
    {
      id: "config_value", 
      label: "Value",
      minWidth: 200,
      align: "center",
      format: (value) => value,
    },
    {
      id: "description",
      label: "Description",
      minWidth: 200,
      align: "center",
      format: (value) => value,
    },
    {
      id: "status_switch",
      label: "Status",
      minWidth: 100,
      align: "center",
      format: (value) => (
        <Switch
          checkedChildren="ON"
          unCheckedChildren="OFF"
          checked={data.find(item => item.id === value)?.status === 1}
          onChange={(checked) => handleStatusChange(value, checked ? 1 : 0)}
          loading={loadingStatus === value}
        />
      ),
    },
    {
      id: "action",
      label: "Actions",
      minWidth: 200,
      align: "center",
      format: (value: any) => {
        const isLoading = loadingEditIds.includes(value);
        const config = data.find(item => item.id === value);
        
        return (
          <Box
            display={"flex"}
            justifyContent={"center"}
            alignItems={"center"}
            gap={2}
          >
            <Box
              size={"small"}
              component={IconButton}
              onClick={() => config && handleManageTranslations(config)}
              title="Translations"
            >
              <GlobalOutlined style={{ fontSize: '20px', color: '#1890ff' }} />
            </Box>
            <Box
              size={"small"}
              component={IconButton}
              disabled={isLoading || loadingDelete}
              onClick={() => handleClickEdit(value)}
            >
              {isLoading ? (
                <span className="anticon" style={{ fontSize: 24 }}>
                  <span className="anticon-spin">
                    <svg viewBox="0 0 1024 1024" focusable="false" data-icon="loading" width="1em" height="1em" fill="currentColor" aria-hidden="true">
                      <path d="M988 548c-19.9 0-36-16.1-36-36 0-59.4-11.6-117-34.6-171.3a440.45 440.45 0 00-94.3-139.9 437.71 437.71 0 00-139.9-94.3C629 83.6 571.4 72 512 72c-19.9 0-36-16.1-36-36s16.1-36 36-36c69.1 0 136.2 13.5 199.3 40.3C772.3 66 827 103 874 150c47 47 83.9 101.8 109.7 162.7 26.7 63.1 40.2 130.2 40.2 199.3.1 19.9-16 36-35.9 36z"></path>
                    </svg>
                  </span>
                </span>
              ) : (
                <EditIcon color="success" />
              )}
            </Box>
            <Popconfirm
              title="Are you sure to delete?"
              okText="Yes"
              cancelText="No"
              onConfirm={() => handleDelete(value)}
              okButtonProps={{
                style: { background: "#ba2156", border: "none" },
                loading: loadingDelete,
              }}
            >
              <Box size={"small"} component={IconButton}>
                <DeleteIcon color={"error"} />
              </Box>
            </Popconfirm>
          </Box>
        );
      },
    },
  ];

  const getDataTable = async (keyword?: string) => {
    setLoadingTable(true);
    try {
      const effectiveKeyword = keyword ?? searchKeyword;
      const response = await axiosInstance.get<SystemConfigListResponse>(pathAPI.systemConfig, {
        params: {
          page,
          size,
          sort: "configKey,asc",
          keyword: effectiveKeyword || undefined,
        },
      });
      if (response.data) {
        const rows = Array.isArray(response.data.data)
          ? response.data.data.map((item) => ({
              ...item,
              action: item.id,
              status_switch: item.id,
            }))
          : [];
        setData(rows);
        setTotal(response.data.total ?? 0);
      }
    } catch (error) {
      console.log("==== get system config error: ", error);
    } finally {
      setLoadingTable(false);
    }
  };

  const handleSearch = () => {
    if (page !== 0) {
      // Page reset triggers the fetch via the page/size effect (single request)
      setPage(0);
    } else {
      getDataTable(searchKeyword);
    }
  };

  useEffect(() => {
    getDataTable();
  }, [page, size]);

  const openModalAddNew = () => {
    console.log("Opening create modal");
    setOpenCreateModal(true);
  };

  return (
    <Card className="user">
      <div className="filter-button mb-3">
        <Space wrap style={{ width: '100%', justifyContent: 'space-between' }}>
          <Space>
            <Box width={300}>
              <SearchInput
                placeholder="Search by keyword..."
                value={searchKeyword}
                onChange={(e) => setSearchKeyword(e.target.value)}
                onSearch={handleSearch}
              />
            </Box>
            <ActionButton
              appSize="large"
              startIcon={<SearchOutlined />}
              onClick={handleSearch}
              loading={loadingTable}
            >
              Search
            </ActionButton>
          </Space>
          <Space>
            <ActionButton appSize="large" startIcon={<PlusCircleOutlined />} onClick={openModalAddNew}>
              Add New
            </ActionButton>
          </Space>
        </Space>
      </div>
      <MuiTable
        header={header}
        data={data}
        isLoading={loadingTable}
        page={page}
        rowsPerPage={size}
        totalElements={total}
        onPageChange={(_, newPage) => setPage(newPage)}
        onRowsPerPageChange={(event) => {
          setSize(Number(event.target.value));
          setPage(0);
        }}
      ></MuiTable>
      {contextHolder}
      
      {/* Create Modal */}
      <CreateSystemConfigModal
        open={openCreateModal}
        reloadData={() => getDataTable(searchKeyword)}
        onCancel={() => setOpenCreateModal(false)}
      />

      {/* Edit Modal */}
      {editingConfig && (
        <EditSystemConfigModal
          open={openEditModal}
          reloadData={() => getDataTable(searchKeyword)}
          onCancel={() => {
            setOpenEditModal(false);
            setEditingConfig(null);
          }}
          configData={editingConfig}
        />
      )}

      {/* Translation Modal */}
      <EndpointTranslationModal
        open={translationModalVisible}
        onClose={() => setTranslationModalVisible(false)}
        endpoint={`${pathAPI.systemConfig}/${selectedConfig?.id}/translations`}
        title="Manage Translations"
        subtitle={selectedConfig?.config_key}
        fallbackSourceText={selectedConfig?.config_value}
      />
    </Card>
  );
};

export default SystemConfig; 